import React, { useState, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';

const SignaturePopup = ({
    isOpen,
    onClose,
    onSave,
    firstName,
    lastName,
    fieldName,
    isMinor = false
}) => {
    const [activeTab, setActiveTab] = useState('font'); // 'font' or 'draw'
    const [minorFirstName, setMinorFirstName] = useState('');
    const [minorLastName, setMinorLastName] = useState('');
    const [showSignatureOptions, setShowSignatureOptions] = useState(false);
    const canvasRef = useRef(null);

    const fullName = isMinor && showSignatureOptions
        ? `${minorFirstName} ${minorLastName}`.trim()
        : `${firstName} ${lastName}`.trim();

    const handleMinorNameSubmit = () => {
        if (!minorFirstName.trim() || !minorLastName.trim()) {
            alert('Please enter both first name and last name');
            return;
        }
        setShowSignatureOptions(true);
        setActiveTab('font');
    };

    const handleSaveFont = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 450;
        canvas.height = 160;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.font = 'italic 56px "Brush Script MT", "Lucida Handwriting", cursive';
        ctx.fillStyle = '#1a1a1a';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillText(fullName, canvas.width / 2, canvas.height / 2);
        const imageData = canvas.toDataURL('image/png');
        onSave(fieldName, imageData, fullName);
        onClose();
    };

    const handleSaveDraw = () => {
        if (canvasRef.current && !canvasRef.current.isEmpty()) {
            const imageData = canvasRef.current.toDataURL('image/png');
            onSave(fieldName, imageData, fullName);
            onClose();
        }
    };

    const handleClearDraw = () => {
        if (canvasRef.current) {
            canvasRef.current.clear();
        }
    };

    const handleBackFromSignatureOptions = () => {
        setShowSignatureOptions(false);
        setMinorFirstName('');
        setMinorLastName('');
        setActiveTab('font');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 overflow-hidden">
                {/* Header */}
                <div className="bg-blue-600 text-white px-6 py-4">
                    <h2 className="text-xl font-semibold">
                        {isMinor && !showSignatureOptions ? "Guardian Signature" : "Sign Document"}
                    </h2>
                    <p className="text-blue-100 text-sm mt-1">
                        {isMinor && !showSignatureOptions
                            ? "Please provide the responsible party's name and then their signature"
                            : "Please provide your signature"}
                    </p>
                </div>

                {/* Tab Navigation */}
                {!isMinor || showSignatureOptions ? (
                    <div className="border-b border-gray-200 px-6">
                        <div className="flex gap-4">
                            <button
                                onClick={() => setActiveTab('font')}
                                className={`py-3 px-4 font-medium border-b-2 ${
                                    activeTab === 'font'
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Use My Name
                            </button>
                            <button
                                onClick={() => setActiveTab('draw')}
                                className={`py-3 px-4 font-medium border-b-2 ${
                                    activeTab === 'draw'
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Draw Signature
                            </button>
                        </div>
                    </div>
                ) : null}

                {/* Content */}
                <div className="px-6 py-8">
                    {isMinor && !showSignatureOptions && (
                        <div>
                            <p className="text-sm text-gray-700 mb-6">
                                Please enter the responsible party's first and last name to proceed with the signature collection.
                            </p>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    value={minorFirstName}
                                    onChange={(e) => setMinorFirstName(e.target.value)}
                                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                                    placeholder="Enter first name"
                                />
                            </div>
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    value={minorLastName}
                                    onChange={(e) => setMinorLastName(e.target.value)}
                                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                                    placeholder="Enter last name"
                                />
                            </div>
                            <button
                                onClick={handleMinorNameSubmit}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition"
                            >
                                Proceed to Signature
                            </button>
                        </div>
                    )}

                    {(!isMinor || showSignatureOptions) && activeTab === 'font' && (
                        <div>
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Signature preview
                                </label>
                                <div
                                    className="w-full h-40 bg-white border-2 border-gray-300 rounded-lg flex items-center justify-center"
                                    style={{
                                        backgroundImage: `
                            repeating-linear-gradient(0deg, #f0f0f0, #f0f0f0 1px, transparent 1px, transparent 20px),
                            repeating-linear-gradient(90deg, #f0f0f0, #f0f0f0 1px, transparent 1px, transparent 20px)
                          `,
                                    }}
                                >
                                    <span
                                        style={{
                                            fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive',
                                            fontSize: '56px',
                                            color: '#1a1a1a',
                                            fontStyle: 'italic',
                                            fontWeight: '300',
                                            letterSpacing: '2px',
                                            fontVariantLigatures: 'common-ligatures',
                                        }}
                                    >
                                        {fullName}
                                    </span>
                                </div>
                            </div>
                            <p className="text-sm text-gray-600 mb-6">
                                Your signature will be generated from your name: <strong>{fullName}</strong>
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleSaveFont}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition"
                                >
                                    Accept This Signature
                                </button>
                                <button
                                    onClick={() => setActiveTab('draw')}
                                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg transition"
                                >
                                    Draw Instead
                                </button>
                            </div>
                        </div>
                    )}

                    {(!isMinor || showSignatureOptions) && activeTab === 'draw' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Draw your signature below
                            </label>
                            <div style={{ touchAction: 'none' }}>
                                <SignatureCanvas
                                    ref={canvasRef}
                                    backgroundColor="white"
                                    penColor="#1a1a1a"
                                    strokeWidth={2}
                                    canvasProps={{
                                        width: 600,
                                        height: 200,
                                        className: 'rounded-lg border-2 border-gray-400 w-full',
                                        style: {
                                            backgroundColor: 'white',
                                            backgroundImage: `
                                repeating-linear-gradient(0deg, #e5e5e5, #e5e5e5 1px, transparent 1px, transparent 20px),
                                repeating-linear-gradient(90deg, #e5e5e5, #e5e5e5 1px, transparent 1px, transparent 20px)
                              `,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                            cursor: 'crosshair',
                                            touchAction: 'none',
                                        },
                                    }}
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-2">Use your mouse or touch screen to draw</p>

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={handleClearDraw}
                                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition"
                                >
                                    Clear
                                </button>
                                <button
                                    onClick={handleSaveDraw}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition"
                                >
                                    Save Signature
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SignaturePopup;
